import { ErrorStringFromException } from "../../utils/utils";
import { DocumentStorage } from "../office-js/DocumentStorage";
import { DocumentStorageError, DocumentStorageErrors } from "../office-js/DocumentStorageErrors";
import { HostAppSettingName, HostAppSettings } from "../office-js/HostAppSettings";

export class DocumentStorageOneNote extends DocumentStorage {

  /*InOneNote Win32, Transcription content is coming as Json object during Transcription pane launch
  but during Transcribing, {HostAppSetting.Set} API save the transcription content as string.
  Hence in order to handle both case and avoid failure, JSON.parse is done in case typeof fetched content from HostAppSetting
  is "string".*/
  GetStoredTranscription(): any {
    try {
      let storedTranscription = HostAppSettings.GetSetting(HostAppSettingName.StoredTranscription);
      const storedTranscriptionJson = (typeof storedTranscription == "string") ? JSON.parse(storedTranscription) : storedTranscription;
      return storedTranscriptionJson;
    } catch (e) {
      throw new DocumentStorageError(DocumentStorageErrors.CorruptTranscriptSource, ErrorStringFromException(e));
    }
  }

  async WriteTranscribeContentToClient(content: string): Promise<void> {

    /* perform update on associated page, if not success in case associated notebookId,
       sectionId or pageId is deleted/moved.*/
    const needCompleteLookup = !await this.UpdateContentToAssociatedPage(content);

    // perform complete lookup for page in all Notebooks and all sections
    if (needCompleteLookup) {
      let isSuccess = await this.PerformCompleteLookupAndUpdate(content);
      if (!isSuccess) {
        throw new DocumentStorageError(DocumentStorageErrors.WriteContentToClientStorageFailed);
      }
    }
  }

  async DeleteTranscribeContentFromClient(): Promise<void> {

    /* perform update on associated page, if not success in case associated notebookId,
       sectionId or pageId is deleted/moved.*/
    const needCompleteLookup = !await this.UpdateContentToAssociatedPage();

    // perform complete lookup for page in all Notebooks and all sections
    if (needCompleteLookup) {
      let isSuccess = await this.PerformCompleteLookupAndUpdate();
      if (!isSuccess) {
        throw new DocumentStorageError(DocumentStorageErrors.DeleteContentFromClientStorageFailed);
      }
    }
  }

  /**
  * UpdateContentToAssociatedPage will update the provided content on Associated Page.
  * @method UpdateContentToAssociatedPage
  * @param {content} string content to be writen, if no content is provided, already existing content on associated page will be cleaned up.
  * @return {boolean} true on success else failure.
  */
  private async UpdateContentToAssociatedPage(content: string = ""): Promise<boolean> {
    let isSuccess = false;
    await OneNote.run(async (context) => {
      try {
        //fetch associated notebook.
        const noteBook = context.application.notebooks.getItem(HostAppSettings.GetSettingAsString(HostAppSettingName.AssociatedOneNoteNotebookId));

        //fetch associated Section.
        const section = noteBook.sections.getItem(HostAppSettings.GetSettingAsString(HostAppSettingName.AssociatedOneNoteSectionId));

        //fetch associated page from associated section.
        const page = section.pages.getItem(HostAppSettings.GetSettingAsString(HostAppSettingName.AssociatedOneNotePageId));

        //update Transcription data to page.
        const transcription = (<any>page).transcription;
        transcription.data = content;
        await context.sync();

        //update result as Success.
        isSuccess = true;
      } catch (e: any) {
        // page not found, update failed.
      }
    });
    return isSuccess;
  }

  private async PerformCompleteLookupAndUpdate(content: string = ""): Promise<boolean> {
    let isSuccess: boolean = false;
    await OneNote.run(async (context) => {

      // lookup for associated page, need to iterate through pages of All session of All notebooks.
      const notebooks = context.application.notebooks;
      notebooks.load('count');
      await context.sync();

      //iterate through notebooks and fetch all sessions for specific notebook.
      for (let noteBookIndex = 0; noteBookIndex < notebooks.count; noteBookIndex++) {
        if (!isSuccess) {
          const noteBook = notebooks.getItem(noteBookIndex);
          const sections = noteBook.sections;
          sections.load('count');
          await context.sync();

          // iterate through sessions and lookup for associated page
          for (let sectionIndex = 0; sectionIndex < sections.count; sectionIndex++) {
            const section = sections.getItem(sectionIndex);

            try {
              let page = section.pages.getItem(HostAppSettings.GetSettingAsString(HostAppSettingName.AssociatedOneNotePageId));
              const transcription = (<any>page).transcription;
              transcription.data = content;
              await context.sync();

              // persist notebook Id
              noteBook.load('id');
              await context.sync();
              HostAppSettings.Set(HostAppSettingName.AssociatedOneNoteNotebookId, noteBook.id);

              //persist section Id
              section.load('id');
              await context.sync();
              HostAppSettings.Set(HostAppSettingName.AssociatedOneNoteSectionId, section.id);

              isSuccess = true;
              break;
            } catch (e: any) {
              // page not present in this session, need to iterate through other session
            }
          }
        }
      }
    });
    return isSuccess;
  }
}